import { createFileRoute, useNavigate, Link } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { useAuth } from "@/hooks/useAuth";
import { useServerFn } from "@tanstack/react-start";
import { Button } from "@/components/ui/button";
import { ArrowLeft, Loader2, RefreshCw, CheckCircle2, AlertTriangle } from "lucide-react";
import { AdminPageShell } from "@/components/dashboard/AdminPageShell";
import { toast } from "sonner";
import {
 getLatestSitemapHealthRun,
 getRecentSitemapHealthRuns,
 getRecentSitemapHealthErrors,
 triggerSitemapHealthcheck,
} from "./-dashboard.admin.sitemap-health.functions";

export const Route = createFileRoute("/dashboard/admin/sitemap-health")({
 head: () => ({
 meta: [
 { title: "Sitemap Health · Heallexa Admin" },
 { name: "robots", content: "noindex, nofollow" },
 ],
 }),
 component: SitemapHealthPage,
});

type Run = {
 id: string;
 started_at: string;
 finished_at: string | null;
 checked_count: number;
 error_count: number;
 ok: boolean;
 cursor_start: number | null;
 cursor_end: number | null;
 mode: string;
};

type ErrorRow = {
 id: string;
 run_id: string;
 url: string;
 status_code: number | null;
 error_type: string;
 message: string | null;
 checked_at: string;
};

const ERROR_TYPES = ["all", "http_4xx", "http_5xx", "invalid_xml", "empty", "timeout", "network", "bad_content_type"] as const;
type Filter = (typeof ERROR_TYPES)[number];

function SitemapHealthPage() {
 const { user, loading: authLoading, primaryRole } = useAuth();
 const navigate = useNavigate();

 const fetchLatest = useServerFn(getLatestSitemapHealthRun);
 const fetchRuns = useServerFn(getRecentSitemapHealthRuns);
 const fetchErrors = useServerFn(getRecentSitemapHealthErrors);
 const trigger = useServerFn(triggerSitemapHealthcheck);

 const [latest, setLatest] = useState<Run | null>(null);
 const [runs, setRuns] = useState<Run[]>([]);
 const [errors, setErrors] = useState<ErrorRow[]>([]);
 const [loading, setLoading] = useState(true);
 const [running, setRunning] = useState(false);
 const [filter, setFilter] = useState<Filter>("all");

 useEffect(() => {
 if (authLoading) return;
 if (!user) { navigate({ to: "/login" }); return; }
 if (primaryRole !== "admin") { navigate({ to: "/dashboard" }); return; }
 void load();
 // eslint-disable-next-line react-hooks/exhaustive-deps
 }, [user, authLoading, primaryRole]);

 const load = async () => {
 setLoading(true);
 try {
 const [l, r, e] = await Promise.all([fetchLatest(), fetchRuns(), fetchErrors()]);
 setLatest(l.run as Run | null);
 setRuns((r.runs ?? []) as Run[]);
 setErrors((e.errors ?? []) as ErrorRow[]);
 } catch (err) {
 toast.error((err as Error).message || "Failed to load health data");
 } finally {
 setLoading(false);
 }
 };

 const runCheck = async (full: boolean) => {
 setRunning(true);
 try {
 const res = await trigger({ data: { full } });
 if (res.status === 200) {
 toast.success(`Check complete · ${(res.body as { error_count?: number })?.error_count ?? 0} errors`);
 } else {
 toast.error(`Check failed (${res.status})`);
 }
 await load();
 } catch (err) {
 toast.error((err as Error).message || "Trigger failed");
 } finally {
 setRunning(false);
 }
 };

 if (authLoading || !user || primaryRole !== "admin") {
 return <div className="min-h-screen grid place-items-center"><Loader2 className="h-6 w-6 animate-spin" /></div>;
 }

 const filteredErrors = filter === "all" ? errors : errors.filter((e) => e.error_type === filter);

  const fmtDate = (d: string) => new Date(d).toLocaleString(undefined, { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" });

  return (
    <AdminPageShell
      title="Sitemap Health"
      description="Internal crawl of /sitemap.xml and all child sitemaps."
      primaryAction={{ label: "Reload", icon: RefreshCw, onClick: load }}
    >
      <div className="space-y-4">
        {/* Latest run summary - compact KPI strip */}
        <div className="rounded-lg border bg-card overflow-hidden">
          <div className="flex items-center gap-2 px-4 py-2.5 border-b bg-muted/30">
            {latest?.ok ? (
              <CheckCircle2 className="h-4 w-4 text-emerald-600" />
            ) : (
              <AlertTriangle className="h-4 w-4 text-amber-600" />
            )}
            <h2 className="text-sm font-medium">Latest run</h2>
            {latest && (
              <span className="text-xs text-muted-foreground ml-auto">
                {fmtDate(latest.started_at)}
              </span>
            )}
          </div>
          {!latest ? (
            <p className="p-4 text-sm text-muted-foreground">No runs yet.</p>
          ) : (
            <div className="grid grid-cols-2 sm:grid-cols-4 divide-x divide-y sm:divide-y-0 border-t-0">
              <Kpi label="Checked" value={latest.checked_count.toLocaleString()} />
              <Kpi label="Errors" value={String(latest.error_count)} tone={latest.error_count > 0 ? "bad" : "good"} />
              <Kpi label="Mode" value={latest.mode} />
              <Kpi label="Cursor" value={latest.cursor_start && latest.cursor_end ? `${latest.cursor_start}→${latest.cursor_end}` : "—"} />
            </div>
          )}
        </div>

        {/* Error filter + table */}
        <div className="rounded-lg border bg-card overflow-hidden">
          <div className="flex items-center justify-between px-4 py-2.5 border-b bg-muted/30 flex-wrap gap-2">
            <h2 className="text-sm font-medium">Errors <span className="text-muted-foreground font-normal">· last 7 days</span></h2>
            <div className="flex gap-1 flex-wrap">
              {ERROR_TYPES.map((t) => {
                const count = t === "all" ? errors.length : errors.filter((e) => e.error_type === t).length;
                const active = filter === t;
                return (
                  <button
                    key={t}
                    onClick={() => setFilter(t)}
                    className={`text-[11px] px-2 py-0.5 rounded-md border transition-colors ${
                      active
                        ? "bg-foreground text-background border-foreground"
                        : "bg-background hover:bg-muted text-muted-foreground"
                    }`}
                  >
                    {t.replace("_", " ")} <span className={active ? "opacity-70" : "opacity-60"}>{count}</span>
                  </button>
                );
              })}
            </div>
          </div>
          {filteredErrors.length === 0 ? (
            <p className="p-6 text-sm text-muted-foreground text-center">No errors in this view.</p>
          ) : (
            <div className="overflow-auto max-h-[420px]">
              <table className="w-full text-xs">
                <thead className="bg-muted/40 sticky top-0 z-10">
                  <tr className="text-muted-foreground">
                    <th className="text-left px-3 py-2 font-medium">URL</th>
                    <th className="text-left px-3 py-2 font-medium w-16">Status</th>
                    <th className="text-left px-3 py-2 font-medium w-32">Type</th>
                    <th className="text-left px-3 py-2 font-medium">Message</th>
                    <th className="text-left px-3 py-2 font-medium w-32">Checked</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredErrors.map((e) => (
                    <tr key={e.id} className="border-t hover:bg-muted/20">
                      <td className="px-3 py-2">
                        <a href={e.url} target="_blank" rel="noreferrer" className="text-primary hover:underline truncate inline-block max-w-[340px]">{e.url}</a>
                      </td>
                      <td className="px-3 py-2 tabular-nums">{e.status_code ?? "—"}</td>
                      <td className="px-3 py-2">
                        <span className="inline-block px-1.5 py-0.5 rounded bg-muted text-[10px] font-mono">{e.error_type}</span>
                      </td>
                      <td className="px-3 py-2 text-muted-foreground truncate max-w-[280px]">{e.message}</td>
                      <td className="px-3 py-2 text-muted-foreground whitespace-nowrap">{fmtDate(e.checked_at)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Run history */}
        <div className="rounded-lg border bg-card overflow-hidden">
          <div className="px-4 py-2.5 border-b bg-muted/30">
            <h2 className="text-sm font-medium">Recent runs</h2>
          </div>
          {runs.length === 0 ? (
            <p className="p-6 text-sm text-muted-foreground text-center">No runs yet.</p>
          ) : (
            <div className="overflow-auto max-h-[420px]">
              <table className="w-full text-xs">
                <thead className="bg-muted/40 sticky top-0 z-10">
                  <tr className="text-muted-foreground">
                    <th className="text-left px-3 py-2 font-medium w-6"></th>
                    <th className="text-left px-3 py-2 font-medium">Started</th>
                    <th className="text-left px-3 py-2 font-medium">Mode</th>
                    <th className="text-right px-3 py-2 font-medium">Checked</th>
                    <th className="text-right px-3 py-2 font-medium">Errors</th>
                    <th className="text-left px-3 py-2 font-medium">Cursor</th>
                  </tr>
                </thead>
                <tbody>
                  {runs.map((r) => (
                    <tr key={r.id} className="border-t hover:bg-muted/20">
                      <td className="px-3 py-2">
                        {r.ok ? (
                          <CheckCircle2 className="h-3.5 w-3.5 text-emerald-600" />
                        ) : (
                          <AlertTriangle className="h-3.5 w-3.5 text-amber-600" />
                        )}
                      </td>
                      <td className="px-3 py-2 whitespace-nowrap">{fmtDate(r.started_at)}</td>
                      <td className="px-3 py-2 text-muted-foreground">{r.mode}</td>
                      <td className="px-3 py-2 text-right tabular-nums">{r.checked_count.toLocaleString()}</td>
                      <td className={`px-3 py-2 text-right tabular-nums ${r.error_count > 0 ? "text-rose-600 font-medium" : "text-muted-foreground"}`}>{r.error_count}</td>
                      <td className="px-3 py-2 text-muted-foreground">
                        {r.cursor_start && r.cursor_end ? `${r.cursor_start}→${r.cursor_end}` : "—"}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </AdminPageShell>
  );
}

function Kpi({ label, value, tone }: { label: string; value: string; tone?: "good" | "bad" }) {
  const color = tone === "good" ? "text-emerald-600" : tone === "bad" ? "text-rose-600" : "";
  return (
    <div className="px-4 py-3">
      <div className="text-[10px] uppercase tracking-wide text-muted-foreground">{label}</div>
      <div className={`text-sm font-semibold mt-0.5 ${color}`}>{value}</div>
    </div>
  );
}
